import {DataTable} from "../DataTable/DataTable";
import {ContainerEntity} from "@ots-frontend/ots-data";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Box, Group, Modal, ModalProps} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {useContainerDataTableFilter} from "./useContainerDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {sortBy} from "lodash";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useMemo} from "react";
import {ContainerStatusIcon} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

const EMPTY_ARRAY: ContainerEntity[] = []

export type ContainerDataTableProps = EntityDataTableProps<ContainerEntity>

export const ContainerDataTable = (props: ContainerDataTableProps) => {
  const {
    records: containers = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useContainerDataTableFilter({
    records: containers
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "name"),
          title: t("common.entity.container.table.column.name", "Name"),
          accessor: 'name',
          width: 100,
          sortable: true,
          render: (record) => (
            <EntityAnchor value={record}/>
          ),
        },
        {
          title: t("common.entity.container.table.column.abbr", "Abbr."),
          accessor: 'abbr',
          width: 100,
          sortable: true,
        },
        {
          ...getColumnFilterProps(filterConfig, "freightType"),
          title: t("common.entity.container.table.column.freighttype", "Freight Type"),
          accessor: 'waterTypeName',
          width: 100,
          sortable: true,
          render: ({waterTypeId, waterTypeName}) => (
            waterTypeId ? <DefaultText value={waterTypeName}/> : undefined
          )
        },
        {
          ...getColumnFilterProps(filterConfig, "barcode"),
          title: t("common.entity.container.table.column.qrcode", "QR Code"),
          accessor: 'barcode',
          width: 100,
          sortable: true,
        },
        {
          title: '',
          accessor: "status",
          width: 100,
          textAlignment: 'right',
          render: (container) => (
            <Group spacing={4} position="right" noWrap>
              {container.doNotUse && (
                <ContainerStatusIcon
                  name={container.clearDoNotUseScheduledDttm ? "do-not-use-scheduled" : "do-not-use"}
                  width={16}
                  height={16}
                />
              )}
              {container.doNotDispatch && (
                <ContainerStatusIcon
                  name={container.clearDoNotDispatchScheduledDttm ? "do-not-dispatch-scheduled" : "do-not-dispatch"}
                  width={16}
                  height={16}
                />
              )}
              {container.dispatchIgnoreMaxNonPrimaryVolume && (
                <ContainerStatusIcon name="ignore-max-non-primary-volume" width={16} height={16}/>
              )}
            </Group>
          )
        }
      ]}
    />
  )
}

export type ContainerDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<ContainerDataTableProps, 'recordsPerPageOptions'>
}

export const ContainerDataTableModal = (props: ContainerDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.container.plural", "Containers"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <ContainerDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
